// src/components/dashboard/employer/employees/EmployeesPage.tsx
// Employer view of active employees, with a unit ↔ organisation scope toggle.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Building2, Briefcase, RefreshCw, Mail } from 'lucide-react';
import { useEmployer } from '@/contexts/EmployerContext';
import { engagementApi } from '@/lib/api/engagementApi';
import type { Employee } from '@/lib/models/engagement';
import { LoadingState, EmptyState } from '@/components/custom-ui/empty-state';
import { ActionButton } from '@/components/custom-ui/action-button';
import { UserAvatar } from '@/components/custom-ui/UserAvatar';
import { NEXILINK_CARD_WHITE_ELEVATED, NEXILINK_SPACING } from '@/lib/nexilink-card-styles';
import { cn } from '@/lib/utils';
import EmployeeDetailModal from './EmployeeDetailModal';

type Scope = 'unit' | 'organisation';

const formatDate = (iso: string): string => {
    try {
        return new Date(iso).toLocaleDateString('sv-SE', {
            year: 'numeric', month: 'short', day: 'numeric',
        });
    } catch {
        return iso;
    }
};

const fullName = (e: Employee): string => {
    const parts = [e.first_name, e.last_name].filter(Boolean);
    return parts.join(' ') || e.email || 'Okänd användare';
};

// Onboarding status chip — shows where each hire is in their onboarding.
const OnboardingChip: React.FC<{ e: Employee }> = ({ e }) => {
    const status = e.onboarding_status ?? 'none';
    const done = e.onboarding_completed ?? 0;
    const total = e.onboarding_total ?? 0;
    const base = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium';
    switch (status) {
        case 'completed':
            return <span className={`${base} bg-green-100 text-green-700`}>✓ Klar</span>;
        case 'in_progress':
            return <span className={`${base} bg-blue-100 text-blue-700`}>Onboardar{total > 0 ? ` · ${done}/${total}` : ''}</span>;
        case 'not_started':
            return <span className={`${base} bg-amber-100 text-amber-700`}>Ej påbörjad</span>;
        default:
            return <span className={`${base} bg-gray-100 text-gray-500`}>Ingen onboarding</span>;
    }
};

const EmployeesPage: React.FC = () => {
    const navigate = useNavigate();
    const { employerDetails, isLoadingProfile } = useEmployer();
    const [scope, setScope] = useState<Scope>('unit');
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selected, setSelected] = useState<Employee | null>(null);

    const unitId = employerDetails?.unit?.id;
    const orgId = employerDetails?.organisation?.id;
    const unitName = employerDetails?.unit?.name;
    const orgName = employerDetails?.organisation?.name;

    const load = async (s: Scope) => {
        if (s === 'unit' && !unitId) return;
        if (s === 'organisation' && !orgId) return;
        setIsLoading(true);
        setError(null);
        try {
            const result = s === 'unit'
                ? await engagementApi.listUnitEmployees(unitId!)
                : await engagementApi.listOrganisationEmployees(orgId!);
            // Sort newest first.
            const sorted = [...result].sort(
                (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
            );
            setEmployees(sorted);
        } catch (err) {
            console.error('[EmployeesPage] load failed:', err);
            setError('Kunde inte hämta medarbetare.');
            setEmployees([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (unitId || orgId) {
            load(scope);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scope, unitId, orgId]);

    if (isLoadingProfile) {
        return <LoadingState message="Laddar..." />;
    }

    return (
        <div className={NEXILINK_SPACING}>
            {/* Title lives in the top header strip (TopHeader). Body keeps the
                action + scope toggle only. */}
            <div className="flex items-center justify-end">
                <ActionButton
                    icon={RefreshCw}
                    onClick={() => load(scope)}
                    disabled={isLoading}
                >
                    Uppdatera
                </ActionButton>
            </div>

            {/* Scope toggle */}
            <div className="inline-flex items-center gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
                <button
                    type="button"
                    onClick={() => setScope('unit')}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        scope === 'unit'
                            ? 'bg-white text-[#1A4D4F] shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                    )}
                >
                    <Briefcase className="w-4 h-4" />
                    Min enhet
                </button>
                <button
                    type="button"
                    onClick={() => setScope('organisation')}
                    className={cn(
                        'flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all',
                        scope === 'organisation'
                            ? 'bg-white text-[#1A4D4F] shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                    )}
                >
                    <Building2 className="w-4 h-4" />
                    Hela organisationen
                </button>
            </div>

            {/* Error banner */}
            {error && (
                <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                    {error}
                </div>
            )}

            {/* Body */}
            {isLoading ? (
                <LoadingState message="Laddar medarbetare..." />
            ) : employees.length === 0 ? (
                <EmptyState
                    icon={Users}
                    title="Inga medarbetare ännu"
                    description="Medarbetare visas här när någon anställs via en jobbansökan eller en accepterad inbjudan. Du kan också bjuda in befintliga anställda."
                    action={{ label: 'Bjud in medarbetare', icon: Mail, onClick: () => navigate('/dashboard/invitations') }}
                />
            ) : (
                <div className={`${NEXILINK_CARD_WHITE_ELEVATED} overflow-hidden`}>
                    <table className="w-full">
                        <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                            <tr>
                                <th className="px-6 py-3 text-left">Medarbetare</th>
                                <th className="px-6 py-3 text-left">Onboarding</th>
                                <th className="px-6 py-3 text-left">E-post</th>
                                <th className="px-6 py-3 text-left">Enhet</th>
                                <th className="px-6 py-3 text-left">Anställd sedan</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {employees.map((e) => (
                                <tr
                                    key={e.engagement_id}
                                    onClick={() => setSelected(e)}
                                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                                >
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-3">
                                            <UserAvatar name={fullName(e)} pictureUrl={e.picture_url} size="md" />
                                            <span className="font-medium text-gray-900">{fullName(e)}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <OnboardingChip e={e} />
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {e.email ? (
                                            <a href={`mailto:${e.email}`} className="text-[#1A4D4F] hover:underline">
                                                {e.email}
                                            </a>
                                        ) : (
                                            <span className="text-gray-400">—</span>
                                        )}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600">
                                        {e.unit_name || <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">
                                        {formatDate(e.started_at)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {selected && (
                <EmployeeDetailModal
                    employee={selected}
                    onClose={() => setSelected(null)}
                    onChanged={() => load(scope)}
                />
            )}
        </div>
    );
};

export default EmployeesPage;
